/**
 * diagram/index.js — редактор блок-схемы.
 *
 * ── Чем он отличается от двух миров, между которыми выбирают ────────────────
 *
 * Mermaid: схема описывается ТЕКСТОМ — её можно сгенерировать, хранить в git,
 * попросить у нейросети. Но подвинуть блок нельзя вообще, поэтому в презентацию
 * такую схему не вставляют.
 *
 * draw.io: можно двигать всё до пикселя. Но текстового источника нет, поэтому
 * схему нельзя ни сгенерировать, ни сравнить версии, ни поправить массово.
 * (Импорт Mermaid у draw.io есть, но перестроение из текста сбрасывает ручную
 * расстановку; у Mermaid Chart и Eraser — то же, проверено 15.09.2026.)
 *
 * Здесь обе стороны живут одновременно и обе редактируемы: текст — чистый
 * Mermaid, ручные сдвиги — отдельный слой дельт (layout.js), который переживает
 * перенабор текста.
 *
 * ── Почему текст — не «кнопка импорта» ─────────────────────────────────────
 * Панель редактируема и схема перерисовывается на каждое нажатие. Отсюда два
 * требования ко всему ниже: разбор обязан быть снисходительным к недописанной
 * строке (см. parse.js), а круговой ход текст → граф → текст обязан сходиться,
 * иначе работа мышью молча стирается набором в панели и наоборот.
 *
 * ── Камера (с 15.09) ─────────────────────────────────────────────────────────
 * Масштаб и сдвиг холста живут в view.js. Схема вписывается сама, пока
 * человек её не приблизил; «вписать» возвращает автоматику. Ручной сдвиг блока
 * камеру НЕ перевписывает: иначе отпущенный блок прыгал бы вместе со всей
 * схемой, потому что изменился её габарит.
 */

import { build } from './scene.js';
import { render, PAD } from './render.js';
import { emit } from './emit.js';
import { parse } from './parse.js';
import { SHAPE_KEYS } from './shapes.js';
import { mountAi } from './ai.js';
import * as View from './view.js';
import { keyOwnedByField } from './keys.js';
import * as Share from './share.js';

const CFG = window.__DG_CFG || {};
const T = CFG.i18n || {};
const SLUG = CFG.slug || 'flowchart';

const $ = (id) => document.getElementById(id);

function t(key, vars) {
    let s = T[key] || key;
    if (vars) {
        for (const k in vars) {
            s = s.split(':' + k).join(String(vars[k]));
        }
    }
    return s;
}

function toast(msg, kind) {
    if (window.__toast) window.__toast(msg, kind || 'info');
}

/* ─────────────────────────────── состояние ─────────────────────────────── */

const state = {
    src: '',
    overrides: {},          // { id: {dx, dy} } — ручные сдвиги, дельтами
    selected: null,         // id блока
    selectedEdge: null,     // id стрелки (e0, e1…) — отдельное пространство имён
    scene: null,
    view: null,             // камера, см. view.js
    palette: 'color',       // палитра файла: color | mono
};

const history = { past: [], future: [] };
const MAX_HISTORY = 60;

function snapshot() {
    return { src: state.src, overrides: JSON.parse(JSON.stringify(state.overrides)) };
}

function pushHistory() {
    history.past.push(snapshot());
    if (history.past.length > MAX_HISTORY) history.past.shift();
    history.future.length = 0;
    syncHistoryButtons();
}

function restore(snap) {
    state.src = snap.src;
    state.overrides = snap.overrides;
    state.selected = null;
    state.selectedEdge = null;
    dom.text.value = state.src;
    draw({ refit: true });
    save();
}

function syncHistoryButtons() {
    if (dom.undo) dom.undo.disabled = history.past.length === 0;
    if (dom.redo) dom.redo.disabled = history.future.length === 0;
}

/* ─────────────────────────────── отрисовка ─────────────────────────────── */

const dom = {};
let drawTimer = null;

function scheduleDraw() {
    clearTimeout(drawTimer);
    drawTimer = setTimeout(() => draw({ refit: true }), 120);
}

function canvasBox() {
    const r = dom.canvas.getBoundingClientRect();
    return { w: Math.max(1, r.width), h: Math.max(1, r.height) };
}

/**
 * Поставить `viewBox` по камере.
 *
 * @param {boolean} refit  перевписать, если камера на автоматике
 */
function applyView(refit) {
    if (!dom.svg || !state.scene) return;
    const box = canvasBox();
    const cw = Number(dom.svg.getAttribute('data-w')) || 1;
    const ch = Number(dom.svg.getAttribute('data-h')) || 1;

    if (!state.view || (refit && state.view.auto)) {
        state.view = View.fit(cw, ch, box.w, box.h);
    }

    dom.svg.setAttribute('viewBox', View.viewBox(state.view, box.w, box.h).join(' '));
    dom.svg.classList.toggle('is-free', !state.view.auto);
    if (dom.zoomPct) dom.zoomPct.textContent = View.percent(state.view) + '%';
    if (dom.zoomFit) dom.zoomFit.setAttribute('aria-pressed', state.view.auto ? 'true' : 'false');
}

function draw(opts) {
    const o = opts || {};
    const scene = build(state.src, state.overrides);
    state.scene = scene;

    if (state.selected && !scene.placed.has(state.selected)) state.selected = null;
    if (state.selectedEdge && !scene.graph.edges.some((e) => e.id === state.selectedEdge)) state.selectedEdge = null;

    render(dom.svg, scene, {
        selected: state.selected,
        selectedEdge: state.selectedEdge,
        interactive: true,
    });
    applyView(!!o.refit);

    // Непонятые строки показываем ТИХО и рядом, а не модалкой: 90% времени они
    // означают «человек ещё печатает», и всплывающая ошибка на каждое нажатие
    // сделала бы панель непригодной.
    const bad = scene.graph.problems || [];
    if (dom.problems) {
        dom.problems.hidden = bad.length === 0;
        dom.problems.textContent = bad.length
            ? t('problem_lines', { n: bad.length, lines: bad.map((p) => p.line).join(', ') })
            : '';
    }

    if (dom.count) {
        dom.count.textContent = t('counts', {
            n: scene.graph.nodes.length,
            e: scene.graph.edges.length,
        });
    }

    if (dom.reset) dom.reset.hidden = Object.keys(state.overrides).length === 0;

    // Переключатель направления берёт состояние ИЗ ТЕКСТА на каждой
    // перерисовке. Иначе он врёт в трёх случаях сразу: схема загружена из
    // прошлого захода, отменена по Ctrl+Z или пришла от нейросети с другим
    // направлением — и кнопка «сверху вниз» горит над горизонтальной схемой.
    //
    // ⚠️ Класс — `on`, как в tool-console (`.tc-seg-b.on`). До 15.09 здесь
    // ставился `is-on`, которого слой не знает, и активное направление не
    // подсвечивалось вообще — обе кнопки выглядели одинаково.
    const dirNow = scene.graph.dir === 'LR' ? 'LR' : 'TD';
    document.querySelectorAll('[data-dir]').forEach((b) => {
        const on = b.getAttribute('data-dir') === dirNow;
        b.classList.toggle('on', on);
        b.setAttribute('aria-pressed', on ? 'true' : 'false');
    });

    syncSelectionUI();

    // Соседние панели (ИИ) слушают это, а не опрашивают состояние по таймеру.
    document.dispatchEvent(new CustomEvent('dg:changed'));
}

function selectedEdgeObj() {
    if (!state.selectedEdge || !state.scene) return null;
    return state.scene.graph.edges.find((e) => e.id === state.selectedEdge) || null;
}

function syncSelectionUI() {
    const hasNode = !!(state.selected && state.scene && state.scene.placed.has(state.selected));
    const edge = selectedEdgeObj();

    if (dom.shapeRow) dom.shapeRow.hidden = !hasNode;
    if (dom.edgeRow) dom.edgeRow.hidden = !edge;
    if (dom.delBtn) dom.delBtn.disabled = !hasNode && !edge;
    if (dom.connectBtn) dom.connectBtn.disabled = !hasNode;

    if (hasNode && dom.shapeSel) {
        const n = state.scene.graph.nodes.find((x) => x.id === state.selected);
        if (n) dom.shapeSel.value = n.shape;
    }
    if (edge) {
        document.querySelectorAll('[data-edge-style]').forEach((b) => {
            const on = b.getAttribute('data-edge-style') === (edge.style || 'solid');
            b.classList.toggle('on', on);
            b.setAttribute('aria-pressed', on ? 'true' : 'false');
        });
    }
}

/* ──────────────────────── правка через граф → текст ────────────────────── */

/**
 * Изменить граф и записать результат обратно в текст.
 *
 * ⚠️ Весь путь идёт ЧЕРЕЗ ТЕКСТ: мы не держим граф как первичное состояние.
 * Иначе появилось бы два источника правды, и первое же расхождение между ними
 * стёрло бы чью-то работу — ровно тот дефект, который эта архитектура
 * существует, чтобы не допустить.
 */
function mutate(fn) {
    const g = parse(state.src);
    const changed = fn(g);
    if (changed === false) {
        return;
    }
    pushHistory();
    state.src = emit(g);
    dom.text.value = state.src;
    draw({ refit: true });
    save();
}

function freshId(graph) {
    const used = new Set(graph.nodes.map((n) => n.id));
    // Буквенные идентификаторы читаются в тексте лучше, чем n1/n2: схему
    // правят руками, и `B --> C` понятнее, чем `n7 --> n8`.
    for (let i = 0; i < 26 * 27; i++) {
        const id = i < 26
            ? String.fromCharCode(65 + i)
            : String.fromCharCode(65 + Math.floor(i / 26) - 1) + String.fromCharCode(65 + (i % 26));
        if (!used.has(id)) return id;
    }
    return 'N' + Date.now().toString(36);
}

function addNode() {
    mutate((g) => {
        const id = freshId(g);
        g.nodes.push({ id, text: t('new_node'), shape: 'process', group: null });
        if (state.selected && g.nodes.some((n) => n.id === state.selected)) {
            g.edges.push({
                id: 'e' + g.edges.length, from: state.selected, to: id,
                label: '', style: 'solid', arrow: 'to',
            });
        }
        state.selected = id;
        state.selectedEdge = null;
    });
}

function deleteSelected() {
    if (state.selectedEdge) {
        const eid = state.selectedEdge;
        mutate((g) => {
            const before = g.edges.length;
            g.edges = g.edges.filter((e) => e.id !== eid);
            state.selectedEdge = null;
            if (g.edges.length === before) return false;
        });
        return;
    }
    if (!state.selected) return;
    const id = state.selected;
    mutate((g) => {
        g.nodes = g.nodes.filter((n) => n.id !== id);
        g.edges = g.edges.filter((e) => e.from !== id && e.to !== id);
        g.groups.forEach((gr) => { gr.nodes = gr.nodes.filter((x) => x !== id); });
        state.selected = null;
    });
    delete state.overrides[id];
}

function setShape(shape) {
    if (!state.selected) return;
    const id = state.selected;
    mutate((g) => {
        const n = g.nodes.find((x) => x.id === id);
        if (!n || n.shape === shape) return false;
        n.shape = shape;
    });
}

function setEdgeStyle(style) {
    const eid = state.selectedEdge;
    if (!eid) return;
    mutate((g) => {
        const e = g.edges.find((x) => x.id === eid);
        if (!e || e.style === style) return false;
        e.style = style;
    });
}

function setDir(dir) {
    mutate((g) => {
        if (g.dir === dir) return false;
        g.dir = dir;
    });
}

function renameNode(id, text) {
    mutate((g) => {
        const n = g.nodes.find((x) => x.id === id);
        if (!n || n.text === text) return false;
        n.text = text;
    });
}

function relabelEdge(eid, text) {
    mutate((g) => {
        const e = g.edges.find((x) => x.id === eid);
        if (!e || (e.label || '') === text) return false;
        e.label = text;
    });
}

/* ──────────────────────── соединение и перетаскивание ──────────────────── */

let connectFrom = null;

function startConnect() {
    connectFrom = state.selected;
    dom.svg.classList.add('is-connecting');
    if (dom.hint) {
        dom.hint.hidden = false;
        dom.hint.textContent = t('connect_hint');
    }
}

function cancelConnect() {
    connectFrom = null;
    dom.svg.classList.remove('is-connecting');
    if (dom.hint) dom.hint.hidden = true;
}

function endConnect(toId) {
    const from = connectFrom;
    cancelConnect();

    if (!from || !toId || from === toId) return;

    mutate((g) => {
        g.edges.push({
            id: 'e' + g.edges.length, from, to: toId,
            label: '', style: 'solid', arrow: 'to',
        });
    });
}

/** Точка указателя относительно холста. */
function localXY(ev) {
    const r = dom.svg.getBoundingClientRect();
    return { x: ev.clientX - r.left, y: ev.clientY - r.top };
}

/** Экранные координаты → координаты схемы (через камеру). */
function toSceneXY(ev) {
    const p = localXY(ev);
    return View.toScene(state.view, p.x, p.y);
}

let drag = null;             // перетаскивание блока
let pan = null;              // перетаскивание фона
const pointers = new Map();  // активные касания — для щипка
let pinch = null;

function onPointerDown(ev) {
    pointers.set(ev.pointerId, localXY(ev));

    // Второй палец — щипок: всё начатое одним пальцем отменяется.
    if (pointers.size === 2) {
        const [a, b] = [...pointers.values()];
        pinch = {
            dist: Math.hypot(a.x - b.x, a.y - b.y) || 1,
            view: state.view,
        };
        drag = null;
        pan = null;
        return;
    }

    const nodeEl = ev.target.closest('[data-node]');
    const edgeEl = nodeEl ? null : ev.target.closest('[data-edge]');

    if (!nodeEl && !edgeEl) {
        tapStart = null;
        if (connectFrom) cancelConnect();
        pan = { x: ev.clientX, y: ev.clientY, view: state.view, moved: false };
        try { dom.svg.setPointerCapture(ev.pointerId); } catch (e) { /* уже отпущен */ }
        return;
    }

    if (edgeEl) {
        if (connectFrom) cancelConnect();
        state.selected = null;
        state.selectedEdge = edgeEl.getAttribute('data-edge');
        tapStart = { kind: 'edge', id: state.selectedEdge, x: ev.clientX, y: ev.clientY };
        draw();
        return;
    }

    const id = nodeEl.getAttribute('data-node');

    if (connectFrom) {
        tapStart = null;
        endConnect(id);
        return;
    }

    tapStart = { kind: 'node', id, x: ev.clientX, y: ev.clientY };

    state.selected = id;
    state.selectedEdge = null;
    draw();

    const p = state.scene.placed.get(id);
    if (!p) return;

    const at = toSceneXY(ev);
    drag = {
        id,
        grabX: at.x - p.x,
        grabY: at.y - p.y,
        baseX: p.x - ((state.overrides[id] && state.overrides[id].dx) || 0),
        baseY: p.y - ((state.overrides[id] && state.overrides[id].dy) || 0),
        moved: false,
    };
    try { dom.svg.setPointerCapture(ev.pointerId); } catch (e) { /* уже отпущен */ }
}

function onPointerMove(ev) {
    if (pointers.has(ev.pointerId)) pointers.set(ev.pointerId, localXY(ev));

    if (pinch && pointers.size === 2) {
        const [a, b] = [...pointers.values()];
        const d = Math.hypot(a.x - b.x, a.y - b.y) || 1;
        const c = { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 };
        state.view = View.zoomAt(pinch.view, d / pinch.dist, c.x, c.y);
        applyView(false);
        return;
    }

    if (pan) {
        const dx = ev.clientX - pan.x;
        const dy = ev.clientY - pan.y;
        if (!pan.moved && Math.hypot(dx, dy) < 4) return;
        pan.moved = true;
        state.view = View.panBy(pan.view, dx, dy);
        applyView(false);
        return;
    }

    if (!drag) return;

    const at = toSceneXY(ev);
    const x = at.x - drag.grabX;
    const y = at.y - drag.grabY;

    if (!drag.moved) {
        if (Math.abs(x - (drag.baseX + ((state.overrides[drag.id] || {}).dx || 0))) < 3
            && Math.abs(y - (drag.baseY + ((state.overrides[drag.id] || {}).dy || 0))) < 3) {
            return;                      // дрожание руки — не перетаскивание
        }
        pushHistory();
        drag.moved = true;
    }

    // ⚠️ Пишем ДЕЛЬТУ от автоместа, а не точку. См. layout.js: точка отрывает
    // блок от схемы на первой же дописанной строке текста.
    state.overrides[drag.id] = {
        dx: Math.round(x - drag.baseX),
        dy: Math.round(y - drag.baseY),
    };
    draw();
}

function onPointerUp(ev) {
    pointers.delete(ev.pointerId);
    if (pinch) {
        if (pointers.size < 2) pinch = null;
        return;
    }
    if (pan) {
        // Нажатие по пустому месту без сдвига — снять выделение.
        if (!pan.moved && (state.selected || state.selectedEdge)) {
            state.selected = null;
            state.selectedEdge = null;
            draw();
        }
        pan = null;
        return;
    }
    if (drag && drag.moved) {
        save();
        tapStart = null;
    }
    drag = null;
    checkDoubleTap(ev);
}

/*
 * Двойное нажатие по блоку или стрелке — правка подписи.
 *
 * ⚠️ Событие dblclick сюда НЕ приходит вовсе, а не приходит «не туда»:
 * pointerdown выделяет элемент и зовёт draw(), который заменяет элементы
 * схемы, и нажатый элемент исчезает из документа до pointerup. Браузеру не на
 * чем собрать click, а без двух click нет и dblclick (замер 16.09: настоящий
 * двойной щелчок даёт pointerdown/pointerup дважды и больше ничего).
 * Синтетический dispatchEvent('dblclick') при этом срабатывал, поэтому
 * обещанная инструкцией правка подписи мышью не открывалась НИКОГДА, а
 * проверка через событие этого не видела. Нажатия считаются здесь; заодно так
 * работает двойное касание на телефоне.
 */
let tapStart = null;
let lastTap = null;
function checkDoubleTap(ev) {
    const s = tapStart;
    tapStart = null;
    if (!s || Math.hypot(ev.clientX - s.x, ev.clientY - s.y) > 8) { lastTap = null; return; }
    const now = performance.now();
    if (lastTap && lastTap.kind === s.kind && lastTap.id === s.id && now - lastTap.t < 400) {
        lastTap = null;
        if (s.kind === 'node') editLabel(s.id); else editEdgeLabel(s.id);
        return;
    }
    lastTap = { kind: s.kind, id: s.id, t: now };
}

function onWheel(ev) {
    // Обычная прокрутка колесом — СТРАНИЦЫ, а не холста: холст занимает
    // полэкрана, и отнять у человека прокрутку над ним значило бы запереть
    // его в инструменте. Масштаб — с Ctrl/⌘ (так же приходит щипок тачпада).
    if (!(ev.ctrlKey || ev.metaKey)) return;
    ev.preventDefault();
    const p = localXY(ev);
    state.view = View.zoomAt(state.view, Math.exp(-ev.deltaY * 0.0022), p.x, p.y);
    applyView(false);
}

function zoomCentre(factor) {
    const box = canvasBox();
    state.view = View.zoomAt(state.view, factor, box.w / 2, box.h / 2);
    applyView(false);
}

function zoomFit() {
    if (state.view) state.view.auto = true;
    applyView(true);
}

function zoomActual() {
    const box = canvasBox();
    state.view = View.zoomAt(state.view, 1 / state.view.s, box.w / 2, box.h / 2);
    applyView(false);
}

/* ───────────────────────── правка подписи на месте ─────────────────────── */

function openInline(value, rect, onDone, allowEmpty) {
    const inp = document.createElement('textarea');
    inp.className = 'dg-inline';
    inp.value = value;
    inp.setAttribute('aria-label', t('edit_label'));

    const box = canvasBox();
    const w = Math.max(120, rect.w);
    const h = Math.max(40, rect.h);
    const left = Math.min(Math.max(0, rect.x + rect.w / 2 - w / 2), Math.max(0, box.w - w));
    const top = Math.min(Math.max(0, rect.y + rect.h / 2 - h / 2), Math.max(0, box.h - h));
    inp.style.left = left + 'px';
    inp.style.top = top + 'px';
    inp.style.width = w + 'px';
    inp.style.height = h + 'px';

    dom.canvas.appendChild(inp);
    inp.focus();
    inp.select();

    let closed = false;
    const done = (commit) => {
        if (closed) return;
        closed = true;
        const v = inp.value.trim();
        inp.remove();
        if (commit && (v || allowEmpty)) onDone(v);
    };

    inp.addEventListener('blur', () => done(true));
    inp.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') { e.preventDefault(); done(false); }
        // Enter применяет, Shift+Enter — перевод строки. Многострочные подписи
        // в блок-схемах обычны, а модальное окно ради них было бы издевательством.
        if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); done(true); }
    });
}

/**
 * ⚠️ Поле ставится В ТОЧКУ, КУДА КАМЕРА РИСУЕТ БЛОК. До 15.09 масштаб считался
 * только по ширине, без полей «meet» и без высоты панели инструментов над
 * холстом — на высокой схеме поле открывалось в стороне от блока. Геометрия
 * держится юнит-тестом tests/js/diagram-view.test.mjs.
 */
function editLabel(id) {
    const p = state.scene.placed.get(id);
    const n = state.scene.graph.nodes.find((x) => x.id === id);
    if (!p || !n) return;

    const a = View.toScreen(state.view, p.x + PAD, p.y + PAD);
    openInline(n.text, { x: a.x, y: a.y, w: p.w * state.view.s, h: p.h * state.view.s },
        (v) => renameNode(id, v), false);
}

function editEdgeLabel(eid) {
    const r = (state.scene.routes || []).find((x) => x.edge.id === eid);
    if (!r) return;
    let pt = r.label;
    if (!pt && r.points && r.points.length) {
        pt = { x: r.points[Math.floor(r.points.length / 2)][0], y: r.points[Math.floor(r.points.length / 2)][1] };
    }
    if (!pt) return;
    const a = View.toScreen(state.view, pt.x + PAD, pt.y + PAD);
    // Пустая подпись у стрелки законна: так её и снимают.
    openInline(r.edge.label || '', { x: a.x - 70, y: a.y - 18, w: 140, h: 36 },
        (v) => relabelEdge(eid, v), true);
}

/* ──────────────────────────────── экспорт ──────────────────────────────── */

function svgString() {
    // Рисуем ту же сцену начисто во временный SVG: экранный несёт выделение,
    // невидимые цели нажатия и камеру, которым в файле делать нечего.
    const tmp = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    tmp.setAttribute('xmlns', 'http://www.w3.org/2000/svg');
    render(tmp, state.scene, { selected: null, selectedEdge: null, light: true });

    const css = document.getElementById(state.palette === 'mono' ? 'dgExportCssMono' : 'dgExportCss')
        || document.getElementById('dgExportCss');
    if (css) {
        const style = document.createElementNS('http://www.w3.org/2000/svg', 'style');
        style.textContent = css.textContent;
        tmp.insertBefore(style, tmp.firstChild);
    }

    tmp.setAttribute('width', tmp.getAttribute('data-w'));
    tmp.setAttribute('height', tmp.getAttribute('data-h'));

    return new XMLSerializer().serializeToString(tmp);
}

function sceneSize() {
    return {
        w: Number(dom.svg.getAttribute('data-w')) || 800,
        h: Number(dom.svg.getAttribute('data-h')) || 600,
    };
}

function fileName(ext) {
    const first = (state.scene && state.scene.graph.nodes[0]);
    const base = window.__outputName
        ? window.__outputName.sanitize((first && first.text) || SLUG)
        : SLUG;
    return (base || SLUG).slice(0, 60) + '.' + ext;
}

async function exportSvg() {
    // ⚠️ Светлая палитра в файле, а не экранная. Схему кладут в документ на
    // белом листе; тёмная тема экрана превратила бы её в чёрный прямоугольник.
    const blob = new Blob([svgString()], { type: 'image/svg+xml;charset=utf-8' });
    window.__downloadFile(blob, fileName('svg'));
}

/** PNG на белом листе. `k` — во сколько раз крупнее экранных единиц схемы. */
async function pngBlob(k) {
    const svg = svgString();
    const { w, h } = sceneSize();

    // Потолок холста браузера: Safari отказывает выше ~16 млн пикселей, и
    // отказ приходит пустым Blob, а не ошибкой.
    const maxK = Math.sqrt(16e6 / Math.max(1, w * h));
    const scale = Math.max(0.5, Math.min(k || 2, maxK));

    const img = new Image();
    const url = URL.createObjectURL(new Blob([svg], { type: 'image/svg+xml;charset=utf-8' }));
    try {
        await new Promise((res, rej) => {
            img.onload = res;
            img.onerror = () => rej(new Error('svg_load'));
            img.src = url;
        });

        const c = document.createElement('canvas');
        c.width = Math.round(w * scale);
        c.height = Math.round(h * scale);
        const g = c.getContext('2d');
        g.fillStyle = '#ffffff';
        g.fillRect(0, 0, c.width, c.height);
        g.drawImage(img, 0, 0, c.width, c.height);

        const blob = await new Promise((res) => c.toBlob(res, 'image/png'));
        if (!blob) throw new Error('png_encode');
        return { blob, width: c.width, height: c.height };
    } finally {
        URL.revokeObjectURL(url);
    }
}

async function exportPng() {
    try {
        const { blob } = await pngBlob(2);
        window.__downloadFile(blob, fileName('png'));
    } catch (e) {
        window.__handleError && window.__handleError(e, SLUG);
    }
}

let pdfLibPromise = null;

function loadPdfLib() {
    if (window.PDFLib) return Promise.resolve(window.PDFLib);
    if (pdfLibPromise) return pdfLibPromise;
    const urls = (CFG.pdfLib && CFG.pdfLib.length) ? CFG.pdfLib : ['/vendor/pdf-lib/pdf-lib.min.js'];
    const p = (window.__assetChain && window.__assetChain.loadScript)
        ? window.__assetChain.loadScript(urls, 'pdf-lib')
        : new Promise((res, rej) => {
            const s = document.createElement('script');
            s.src = urls[0];
            s.async = true;
            s.onload = res;
            s.onerror = () => rej(new Error('pdf-lib failed to load'));
            document.head.appendChild(s);
        });
    pdfLibPromise = p.then(() => {
        if (!window.PDFLib) throw new Error('pdf-lib failed to load');
        return window.PDFLib;
    }).catch((e) => { pdfLibPromise = null; throw e; });
    return pdfLibPromise;
}

/**
 * PDF на лист A4 — то, что сдают в курсовой и вкладывают в регламент.
 *
 * Внутри — PNG в разрешении ~300 точек на дюйм по ширине поля листа, а не
 * вектор: pdf-lib не умеет рисовать SVG, а растровая копия с таким
 * разрешением печатается без ступенек. Вектор для вёрстки — кнопка SVG рядом.
 * Ориентация листа — по форме схемы. Маленькую схему НЕ растягиваем на весь
 * лист: подписи крупнее 14 пт выглядят как плакат.
 */
async function exportPdf() {
    const btn = dom.pdfBtn;
    if (btn) btn.disabled = true;
    try {
        const PDFLib = await loadPdfLib();
        const { w, h } = sceneSize();
        const landscape = w > h * 1.15;
        const pageW = landscape ? 841.89 : 595.28;
        const pageH = landscape ? 595.28 : 841.89;
        const margin = 42.5;                     // 15 мм
        const aw = pageW - margin * 2;
        const ah = pageH - margin * 2;

        const ptPerUnit = Math.min(aw / w, ah / h, 1);
        const k = Math.min(5, Math.max(1, (aw / 72 * 300) / w));
        const { blob } = await pngBlob(k);

        const doc = await PDFLib.PDFDocument.create();
        const page = doc.addPage([pageW, pageH]);
        const png = await doc.embedPng(await blob.arrayBuffer());
        const dw = w * ptPerUnit;
        const dh = h * ptPerUnit;
        page.drawImage(png, {
            x: (pageW - dw) / 2,
            y: pageH - margin - dh,
            width: dw,
            height: dh,
        });
        doc.setTitle(fileName('pdf').replace(/\.pdf$/, ''));
        doc.setCreator('Timbrica');

        const bytes = await doc.save();
        window.__downloadFile(new Blob([bytes], { type: 'application/pdf' }), fileName('pdf'));
    } catch (e) {
        toast(t('pdf_failed'), 'error');
        window.__handleError && window.__handleError(e, SLUG);
    } finally {
        if (btn) btn.disabled = false;
    }
}

function canCopyImage() {
    return !!(window.ClipboardItem && navigator.clipboard && navigator.clipboard.write);
}

/**
 * Картинку — прямо в буфер обмена: вставить в Word, Google Docs или чат одним
 * Ctrl+V, не проходя через «Загрузки».
 *
 * ⚠️ В ClipboardItem уходит ОБЕЩАНИЕ Blob, а не готовый Blob: Safari отдаёт
 * буфер только синхронно внутри нажатия, и `await` перед `write` у него
 * отказывает. Chrome и Firefox принимают обещание так же.
 */
function copyImage() {
    if (!canCopyImage()) {
        toast(t('copy_image_failed'), 'error');
        return;
    }
    const pending = pngBlob(2).then((r) => r.blob);
    navigator.clipboard.write([new ClipboardItem({ 'image/png': pending })]).then(
        () => toast(t('copied_image'), 'success'),
        () => toast(t('copy_image_failed'), 'error'),
    );
}

/** Текст в буфер — тем же путём обещания, чтобы пережить `await` в Safari. */
function writeClipboardText(pendingText) {
    if (window.ClipboardItem && navigator.clipboard && navigator.clipboard.write) {
        const blob = Promise.resolve(pendingText).then((s) => new Blob([s], { type: 'text/plain' }));
        return navigator.clipboard.write([new ClipboardItem({ 'text/plain': blob })])
            .catch(() => Promise.resolve(pendingText).then((s) => navigator.clipboard.writeText(s)));
    }
    return Promise.resolve(pendingText).then((s) => navigator.clipboard.writeText(s));
}

function copyText() {
    writeClipboardText(state.src).then(
        () => toast(t('copied'), 'success'),
        () => toast(t('copy_failed'), 'error'),
    );
}

/** Блок кода, который GitHub, GitLab, Notion и Obsidian рисуют схемой. */
function copyMarkdown() {
    writeClipboardText('```mermaid\n' + state.src.trim() + '\n```\n').then(
        () => toast(t('copied_md'), 'success'),
        () => toast(t('copy_failed'), 'error'),
    );
}

function shareUrl(frag) {
    return location.origin + location.pathname + location.search + '#' + frag;
}

async function shareLink() {
    const pending = Share.encode({ src: state.src, overrides: state.overrides }).then(shareUrl);

    // На телефоне — системное «Поделиться»: ссылку отправляют в мессенджер, и
    // лишний шаг «скопировать → открыть чат → вставить» там дорог.
    const coarse = window.matchMedia && window.matchMedia('(pointer:coarse)').matches;
    if (coarse && navigator.share) {
        try {
            await navigator.share({ title: document.title, url: await pending });
            return;
        } catch (e) {
            if (e && e.name === 'AbortError') return;
            // Нет активации или цель отказала — падаем на буфер обмена.
        }
    }

    writeClipboardText(pending).then(
        () => toast(t('share_copied'), 'success'),
        () => toast(t('copy_failed'), 'error'),
    );
}

/* ──────────────────────────── сохранение ───────────────────────────────── */

function save() {
    if (!window.__toolSettings) return;
    window.__toolSettings.save(SLUG, 'src', state.src);
    // ⚠️ Объект как есть: __toolSettings сам делает JSON.stringify. Здесь стоял
    // ещё один stringify, и сдвиги лежали в хранилище закодированными ДВАЖДЫ —
    // редактор это переживал (load так же декодировал дважды), а сторож,
    // читавший хранилище одним JSON.parse, получал строку "{}" и обвинял
    // исправный код в потере сдвигов (11.09).
    window.__toolSettings.save(SLUG, 'overrides', state.overrides);
}

function load() {
    if (!window.__toolSettings) return null;
    const src = window.__toolSettings.load(SLUG, 'src', '');
    let overrides = window.__toolSettings.load(SLUG, 'overrides', {});

    // Терпим и прежнюю двойную запись: снимаем лишние слои строк. Тул ни дня
    // не был публичным, но чужой браузер разработчика мог её сохранить.
    try {
        for (let i = 0; i < 3 && typeof overrides === 'string'; i++) {
            overrides = JSON.parse(overrides || '{}');
        }
    } catch (e) {
        overrides = {};
    }
    if (!overrides || typeof overrides !== 'object') {
        overrides = {};
    }

    return src ? { src, overrides } : null;
}

/* ────────────────────────────── клавиатура ─────────────────────────────── */

function focusOnCanvas(target) {
    return !target || target === document.body || target === document.documentElement
        || (dom.canvas && dom.canvas.contains(target));
}

function nudgeSelected(dx, dy, repeat) {
    const id = state.selected;
    if (!id) return;
    if (!repeat) pushHistory();
    const cur = state.overrides[id] || { dx: 0, dy: 0 };
    state.overrides[id] = { dx: (cur.dx || 0) + dx, dy: (cur.dy || 0) + dy };
    draw();
    save();
}

function onKey(ev) {
    // ⚠️ Клавиша в поле ввода — поля (см. keys.js). До 15.09 исключением была
    // одна текстовая панель, и Backspace в поле «Опишите процесс» удалял блок.
    if (keyOwnedByField(ev.target, ev.key)) {
        return;
    }

    const mod = ev.ctrlKey || ev.metaKey;

    if (mod && ev.key.toLowerCase() === 'z') {
        ev.preventDefault();
        if (ev.shiftKey) doRedo(); else doUndo();
        return;
    }
    if (mod && ev.key.toLowerCase() === 'y') {
        ev.preventDefault();
        doRedo();
        return;
    }
    if (mod || ev.altKey) return;

    if (ev.key === 'Escape') {
        if (connectFrom) cancelConnect();
        if (state.selected || state.selectedEdge) {
            state.selected = null;
            state.selectedEdge = null;
            draw();
        }
        return;
    }

    const onCanvas = focusOnCanvas(ev.target);

    if (onCanvas && (ev.key === '+' || ev.key === '=')) { ev.preventDefault(); zoomCentre(1.25); return; }
    if (onCanvas && ev.key === '-') { ev.preventDefault(); zoomCentre(0.8); return; }
    if (onCanvas && ev.key === '0') { ev.preventDefault(); zoomFit(); return; }

    if (state.selectedEdge) {
        if (ev.key === 'Delete' || ev.key === 'Backspace') {
            ev.preventDefault();
            deleteSelected();
        } else if (ev.key === 'Enter') {
            ev.preventDefault();
            editEdgeLabel(state.selectedEdge);
        }
        return;
    }

    if (!state.selected) return;

    if (ev.key === 'Delete' || ev.key === 'Backspace') {
        ev.preventDefault();
        deleteSelected();
    } else if (ev.key === 'Enter') {
        ev.preventDefault();
        editLabel(state.selected);
    } else if (ev.key === 'Tab' && onCanvas) {
        // Tab делает следующий шаг — та же грамматика, что в mind-map: схема
        // строится с клавиатуры так же быстро, как думается. Только когда
        // фокус на холсте: на кнопке Tab остаётся переходом по странице.
        ev.preventDefault();
        addNode();
    } else if (onCanvas && ev.key.startsWith('Arrow')) {
        // Сдвиг стрелками — тот же ручной сдвиг, что мышью, для тех, кто
        // не может или не хочет тащить.
        ev.preventDefault();
        const step = ev.shiftKey ? 40 : 10;
        const d = { ArrowLeft: [-step, 0], ArrowRight: [step, 0], ArrowUp: [0, -step], ArrowDown: [0, step] }[ev.key];
        if (d) nudgeSelected(d[0], d[1], ev.repeat);
    }
}

function doUndo() {
    if (!history.past.length) return;
    history.future.push(snapshot());
    restore(history.past.pop());
    syncHistoryButtons();
}

function doRedo() {
    if (!history.future.length) return;
    history.past.push(snapshot());
    restore(history.future.pop());
    syncHistoryButtons();
}

function resetNudges() {
    if (!Object.keys(state.overrides).length) return;
    pushHistory();
    state.overrides = {};
    draw({ refit: true });
    save();
}

/* ─────────────────────────────── вкладки ───────────────────────────────── */

function mountTabs() {
    const tabs = [...document.querySelectorAll('[data-dg-tab]')];
    if (!tabs.length) return;

    const select = (name, focus) => {
        tabs.forEach((b) => {
            const on = b.getAttribute('data-dg-tab') === name;
            b.setAttribute('aria-selected', on ? 'true' : 'false');
            b.tabIndex = on ? 0 : -1;
            b.classList.toggle('on', on);
            const panel = document.getElementById(b.getAttribute('aria-controls'));
            if (panel) panel.hidden = !on;
            if (on && focus) b.focus();
        });
        if (window.__toolSettings) window.__toolSettings.save(SLUG, 'tab', name);
    };

    tabs.forEach((b, i) => {
        b.addEventListener('click', () => select(b.getAttribute('data-dg-tab'), false));
        b.addEventListener('keydown', (e) => {
            const dir = e.key === 'ArrowRight' ? 1 : e.key === 'ArrowLeft' ? -1 : 0;
            if (!dir) return;
            e.preventDefault();
            // В RTL стрелка «вправо» ведёт к предыдущей вкладке.
            const rtl = document.documentElement.dir === 'rtl';
            const next = tabs[(i + (rtl ? -dir : dir) + tabs.length) % tabs.length];
            select(next.getAttribute('data-dg-tab'), true);
        });
    });

    const names = tabs.map((b) => b.getAttribute('data-dg-tab'));
    const saved = window.__toolSettings ? window.__toolSettings.load(SLUG, 'tab', 'text') : 'text';
    select(names.includes(saved) ? saved : names[0], false);
}

/** Показать вкладку по имени (снаружи — например, после «Схема открыта по ссылке»). */
function showTab(name) {
    const b = document.querySelector('[data-dg-tab="' + name + '"]');
    if (b) b.click();
}

/* ───────────────────────────────── старт ───────────────────────────────── */

function applyPalette(name) {
    state.palette = name === 'mono' ? 'mono' : 'color';
    document.querySelectorAll('[data-palette]').forEach((b) => {
        const on = b.getAttribute('data-palette') === state.palette;
        b.classList.toggle('on', on);
        b.setAttribute('aria-pressed', on ? 'true' : 'false');
    });
}

async function openSharedLink(savedSnapshot) {
    if (!Share.hasShared(location.hash)) return;
    const doc = await Share.decode(location.hash);

    // Адрес больше не нужен: иначе перезагрузка страницы снова подменила бы
    // схему, над которой человек уже поработал.
    try {
        window.history.replaceState(null, '', location.pathname + location.search);
    } catch (e) { /* file: и песочницы */ }

    if (!doc) {
        toast(t('shared_broken'), 'warning');
        return;
    }
    if (doc.src === state.src && JSON.stringify(doc.overrides) === JSON.stringify(state.overrides)) {
        return;
    }

    // Своя схема не пропадает: она — шаг назад в истории.
    if (savedSnapshot) {
        history.past.push(savedSnapshot);
        history.future.length = 0;
    }
    state.src = doc.src;
    state.overrides = doc.overrides;
    state.selected = null;
    state.selectedEdge = null;
    dom.text.value = state.src;
    syncHistoryButtons();
    draw({ refit: true });
    save();
    toast(savedSnapshot ? t('shared_opened_undo') : t('shared_opened'), 'info');
    showTab('text');
}

function boot() {
    dom.svg = $('dgSvg');
    dom.canvas = $('dgCanvas');
    dom.stage = $('dgStage');
    dom.text = $('dgText');
    dom.problems = $('dgProblems');
    dom.count = $('dgCount');
    dom.hint = $('dgHint');
    dom.shapeRow = $('dgShapeRow');
    dom.shapeSel = $('dgShape');
    dom.edgeRow = $('dgEdgeRow');
    dom.delBtn = $('dgDelete');
    dom.connectBtn = $('dgConnect');
    dom.undo = $('dgUndo');
    dom.redo = $('dgRedo');
    dom.reset = $('dgReset');
    dom.zoomPct = $('dgZoomPct');
    dom.zoomFit = $('dgZoomFit');
    dom.pdfBtn = $('dgPdfBtn');

    if (!dom.svg || !dom.text || !dom.canvas) return;

    // Формы в выпадающем списке — из реестра, а не рукописным списком: вторая
    // копия разъедется с движком молча.
    if (dom.shapeSel) {
        SHAPE_KEYS.forEach((k) => {
            const o = document.createElement('option');
            o.value = k;
            o.textContent = t('shape_' + k);
            dom.shapeSel.appendChild(o);
        });
    }

    const saved = load();
    state.src = (saved && saved.src) || dom.text.value || CFG.sample || '';
    state.overrides = (saved && saved.overrides) || {};
    dom.text.value = state.src;

    dom.text.addEventListener('input', () => {
        state.src = dom.text.value;
        scheduleDraw();
        save();
    });
    // История по тексту пишется на паузе, а не на каждое нажатие: иначе Ctrl+Z
    // отменял бы по одной букве, и вернуться на шаг назад было бы невозможно.
    dom.text.addEventListener('blur', () => pushHistory());

    dom.svg.addEventListener('pointerdown', onPointerDown);
    dom.svg.addEventListener('pointermove', onPointerMove);
    dom.svg.addEventListener('pointerup', onPointerUp);
    dom.svg.addEventListener('pointercancel', onPointerUp);
    dom.svg.addEventListener('wheel', onWheel, { passive: false });
    // ⚠️ Настоящий двойной щелчок приходит сюда с target = сам <svg>, а не блок:
    // pointerdown выделяет блок и зовёт draw(), который ЗАМЕНЯЕТ элементы
    // схемы, — и браузер отдаёт dblclick общему предку двух уже разных
    // элементов. Правка подписи мышью так не открывалась никогда, а
    // синтетическое событие (без pointerdown) открывало её исправно — поэтому
    // дефект не был виден проверке с dispatchEvent. Блок ищем и по точке.
    dom.svg.addEventListener('dblclick', (ev) => {
        const under = document.elementFromPoint(ev.clientX, ev.clientY);
        const pick = (sel) => ev.target.closest(sel) || (under && under.closest(sel));
        const g = pick('[data-node]');
        if (g) { editLabel(g.getAttribute('data-node')); return; }
        const e = pick('[data-edge]');
        if (e) editEdgeLabel(e.getAttribute('data-edge'));
    });

    document.addEventListener('keydown', onKey);

    $('dgAdd') && $('dgAdd').addEventListener('click', addNode);
    dom.delBtn && dom.delBtn.addEventListener('click', deleteSelected);
    dom.connectBtn && dom.connectBtn.addEventListener('click', startConnect);
    dom.undo && dom.undo.addEventListener('click', doUndo);
    dom.redo && dom.redo.addEventListener('click', doRedo);
    dom.reset && dom.reset.addEventListener('click', resetNudges);
    dom.shapeSel && dom.shapeSel.addEventListener('change', () => setShape(dom.shapeSel.value));
    $('dgEdgeLabel') && $('dgEdgeLabel').addEventListener('click', () => {
        if (state.selectedEdge) editEdgeLabel(state.selectedEdge);
    });
    document.querySelectorAll('[data-edge-style]').forEach((b) => {
        b.addEventListener('click', () => setEdgeStyle(b.getAttribute('data-edge-style')));
    });

    $('dgZoomIn') && $('dgZoomIn').addEventListener('click', () => zoomCentre(1.25));
    $('dgZoomOut') && $('dgZoomOut').addEventListener('click', () => zoomCentre(0.8));
    dom.zoomFit && dom.zoomFit.addEventListener('click', zoomFit);
    dom.zoomPct && dom.zoomPct.addEventListener('click', zoomActual);

    document.querySelectorAll('[data-dir]').forEach((b) => {
        b.addEventListener('click', () => setDir(b.getAttribute('data-dir')));
    });

    $('dgSvgBtn') && $('dgSvgBtn').addEventListener('click', exportSvg);
    $('dgPngBtn') && $('dgPngBtn').addEventListener('click', exportPng);
    dom.pdfBtn && dom.pdfBtn.addEventListener('click', exportPdf);
    $('dgCopyBtn') && $('dgCopyBtn').addEventListener('click', copyText);
    $('dgMdBtn') && $('dgMdBtn').addEventListener('click', copyMarkdown);
    $('dgShareBtn') && $('dgShareBtn').addEventListener('click', shareLink);
    const imgBtn = $('dgCopyImgBtn');
    if (imgBtn) {
        // Нет API — нет кнопки: мёртвая кнопка хуже отсутствующей.
        imgBtn.hidden = !canCopyImage();
        imgBtn.addEventListener('click', copyImage);
    }

    const savedPalette = window.__toolSettings ? window.__toolSettings.load(SLUG, 'palette', 'color') : 'color';
    applyPalette(savedPalette);
    document.querySelectorAll('[data-palette]').forEach((b) => {
        b.addEventListener('click', () => {
            applyPalette(b.getAttribute('data-palette'));
            if (window.__toolSettings) window.__toolSettings.save(SLUG, 'palette', state.palette);
        });
    });

    document.querySelectorAll('[data-sample]').forEach((b) => {
        b.addEventListener('click', () => {
            pushHistory();
            state.src = b.getAttribute('data-sample-src') || '';
            state.overrides = {};
            state.selected = null;
            state.selectedEdge = null;
            dom.text.value = state.src;
            if (state.view) state.view.auto = true;
            draw({ refit: true });
            save();
        });
    });

    mountTabs();

    // ИИ-панель получает ровно те рычаги, что ей нужны, а не весь редактор:
    // так видно, что именно она может сделать со схемой.
    mountAi({
        getSrc: () => state.src,
        getDir: () => (parse(state.src).dir === 'LR' ? 'LR' : 'TD'),
        t,
        setSrc: (text, keepNudges) => {
            pushHistory();
            state.src = text;
            // Постройка с нуля приносит новые id — старые сдвиги легли бы на
            // чужие блоки. Правка словами id сохраняет, и сдвиги обязаны её
            // пережить (см. ai.js и FlowchartHandler).
            if (!keepNudges) state.overrides = {};
            state.selected = null;
            state.selectedEdge = null;
            dom.text.value = text;
            if (state.view) state.view.auto = true;
            draw({ refit: true });
            save();
        },
    });

    // Холст меняет размер вместе с окном и раскладкой вкладок — камера
    // перевписывается, пока человек её не трогал.
    if (window.ResizeObserver) {
        new ResizeObserver(() => applyView(true)).observe(dom.canvas);
    }

    syncHistoryButtons();
    draw({ refit: true });

    openSharedLink(saved ? { src: saved.src, overrides: JSON.parse(JSON.stringify(saved.overrides)) } : null);
}

if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
} else {
    boot();
}
